import { Component } from '@theme/component';
import { API_ORIGIN, PAGE_SIZE, text, validId, normalName, normalCard, matchIdentity, stateFromURL, stateURL, cardQuery, readJSON } from './tcg-dex-core.js';

class TcgPokedex extends Component {
  #abort;
  #request;
  #version = 0;
  #cache = new Map();
  #species = [];
  #sets = [];
  #cards = [];
  #labels;
  #state;
  #lastLanguage;
  #spriteCache = new Map();
  #spriteURLs = {};

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    this.#spriteURLs = JSON.parse(this.querySelector('[data-dex-sprites]')?.textContent || '{}');
    this.#labels = JSON.parse(this.querySelector('[data-dex-labels]').textContent);
    this.#state = stateFromURL(location.href);
    const signal = this.#abort.signal;
    this.querySelector('[data-dex-form]').addEventListener('submit', event => {
      event.preventDefault();
      this.#state.q = this.querySelector('[data-dex-query]').value.trim().slice(0, 80);
      this.#state.page = 1; this.#navigate();
    }, { signal });
    this.addEventListener('click', this.#click, { signal });
    this.querySelectorAll('[data-dex-filter]').forEach(select => select.addEventListener('change', () => {
      this.#state[select.dataset.dexFilter] = select.value;
      if (select.dataset.dexFilter === 'language') { this.#state.set = ''; this.#state.rarity = ''; }
      this.#state.page = 1; this.#navigate();
    }, { signal }));
    window.addEventListener('popstate', () => { this.#state = stateFromURL(location.href); this.#load(); }, { signal });
    this.#boot();
  }

  disconnectedCallback() {
    this.#abort?.abort(); this.#request?.abort(); this.#version++;
    this.querySelector('[data-dex-dialog]')?.close();
    super.disconnectedCallback();
  }

  #node(tag, className, value) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (value !== undefined) node.textContent = String(value);
    return node;
  }

  #message(key) { return this.#labels[key] || key; }

  #status(key, error = false) {
    const region = this.querySelector('[data-dex-status]');
    region.textContent = this.#message(key);
    region.dataset.error = String(error);
  }

  async #boot() {
    try {
      const index = await readJSON(this.dataset.speciesUrl, { external: false, signal: this.#abort.signal });
      this.#species = Array.isArray(index.species) ? index.species.filter(p => Number.isSafeInteger(p.id) && p.id > 0 && typeof p.name === 'string').slice(0, 10000) : [];
      this.#options();
      await this.#load();
    } catch {
      if (!this.#abort.signal.aborted) this.#status('unavailable', true);
    }
  }

  #options() {
    const list = this.querySelector('datalist');
    list.replaceChildren(...this.#species.map(p => {
      const option = this.#node('option'); option.value = p.name; option.label = `#${String(p.id).padStart(4, '0')}`; return option;
    }));
  }

  #navigate() {
    history.pushState({ ...history.state, pokedex: true }, '', stateURL(location.href, this.#state));
    this.#load();
  }

  #click = event => {
    const target = event.target instanceof Element ? event.target.closest('button') : null;
    if (!target || !this.contains(target)) return;
    if (target.dataset.dexStep) {
      const current = this.#species.findIndex(p => normalName(p.name) === normalName(this.#state.q) || String(p.id) === this.#state.q.replace('#', ''));
      const next = Math.max(0, Math.min(this.#species.length - 1, Math.max(0, current) + Number(target.dataset.dexStep)));
      this.#state.q = this.#species[next].name; this.#state.page = 1; this.#navigate(); return;
    }
    if (target.hasAttribute('data-dex-close')) { this.querySelector('[data-dex-dialog]').close(); return; }
    if (target.dataset.dexMode) {
      this.#state.mode = target.dataset.dexMode;
      history.pushState({ ...history.state, pokedex: true }, '', stateURL(location.href, this.#state));
      this.#paintState();
      if (this.#state.mode === 'classic' && this.#cards[0]) this.#spotlight(this.#cards.find(c => c.types.length && c.hp) || this.#cards[0]);
    } else if (target.dataset.dexSpecies) {
      this.#state.q = target.dataset.dexSpecies; this.#state.page = 1; this.#navigate();
    } else if (target.hasAttribute('data-dex-retry')) this.#load();
    else if (target.dataset.dexPage) {
      this.#state.page = Math.max(1, this.#state.page + Number(target.dataset.dexPage)); this.#navigate();
    } else if (target.dataset.dexCard) {
      const card = this.#cards.find(c => c.id === target.dataset.dexCard);
      if (card) {
        if (this.#state.mode === 'collector') {
          const dialog = this.querySelector('[data-dex-dialog]');
          this.#spotlight(card, this.querySelector('[data-dex-dialog-content]'));
          dialog.setAttribute('aria-label', `${this.#message('details')} ${card.name}`);
          dialog.showModal();
        } else { this.#spotlight(card); this.querySelector('[data-dex-focus]').focus({ preventScroll: true }); }
      }
    }
  };

  #paintState() {
    this.dataset.mode = this.#state.mode;
    this.querySelector('[data-dex-query]').value = this.#state.q;
    for (const select of this.querySelectorAll('[data-dex-filter]')) select.value = this.#state[select.dataset.dexFilter];
    for (const button of this.querySelectorAll('[data-dex-mode]')) button.setAttribute('aria-pressed', String(button.dataset.dexMode === this.#state.mode));
    this.querySelector('[data-dex-prev]').disabled = this.#state.page <= 1;
    this.querySelector('[data-dex-page-label]').textContent = `${this.#message('page')} ${this.#state.page}`;
    const matches = this.#species.filter(p => !this.#state.q || normalName(p.name).includes(normalName(this.#state.q)) || String(p.id) === this.#state.q.replace('#', ''));
    const suggestions = matches.length ? matches.slice(0, 8) : this.#species.filter(p => [6, 25, 133, 151, 1017, 1025].includes(p.id));
    this.querySelector('[data-dex-species-list]').replaceChildren(...suggestions.map(p => {
      const b = this.#node('button', 'pdx-species', `#${String(p.id).padStart(4, '0')}  ${p.name}`);
      b.type = 'button'; b.dataset.dexSpecies = p.name;
      b.setAttribute('aria-pressed', String(normalName(p.name) === normalName(this.#state.q) || String(p.id) === this.#state.q.replace('#', '')));
      return b;
    }));
  }

  async #loadFilters(signal, language) {
    const [sets, rarities, ids] = await Promise.all([
      readJSON(`${API_ORIGIN}/v2/${language}/sets`, { signal }),
      readJSON(`${API_ORIGIN}/v2/${language}/rarities`, { signal }),
      readJSON(`${API_ORIGIN}/v2/${language}/dex-ids`, { signal }),
    ]);
    if (!Array.isArray(sets) || !Array.isArray(rarities) || !Array.isArray(ids)) throw new Error('Invalid filters');
    this.#sets = sets.filter(s => validId(s.id) && typeof s.name === 'string').slice(0, 2000);
    const addOptions = (key, values, allLabel) => {
      const select = this.querySelector(`[data-dex-filter="${key}"]`);
      const options = [this.#node('option', '', allLabel)]; options[0].value = '';
      for (const [value, label] of values) { const option = this.#node('option', '', label); option.value = value; options.push(option); }
      select.replaceChildren(...options);
    };
    addOptions('set', this.#sets.slice().reverse().map(s => [s.id, `${text(s.name)} · ${s.id}`]), this.#message('all_sets'));
    addOptions('rarity', rarities.filter(r => typeof r === 'string' && r.length <= 80).slice(0, 150).map(r => [r, r]), this.#message('all_rarities'));
    const existing = new Set(this.#species.map(p => p.id));
    for (const id of ids.slice(0, 10000)) if (Number.isSafeInteger(id) && id > 0 && id < 100000 && !existing.has(id)) this.#species.push({ id, name: `#${id}` });
    this.#lastLanguage = language;
    this.#options();
    this.querySelector('[data-dex-coverage]').textContent = `${this.#species.length} ${this.#message('pokemon')} · ${this.#sets.length} ${this.#message('sets')}`;
  }

  async #load() {
    this.#request?.abort(); this.#request = new AbortController();
    const signal = this.#request.signal; const version = ++this.#version;
    this.#paintState(); this.#status('loading');
    this.setAttribute('aria-busy', 'true');
    this.querySelector('[data-dex-results]').replaceChildren();
    this.querySelector('[data-dex-focus]').replaceChildren();
    this.querySelector('[data-dex-next]').disabled = true;
    this.querySelector('[data-dex-retry]').hidden = true;
    try {
      if (this.#lastLanguage !== this.#state.language) await this.#loadFilters(signal, this.#state.language);
      if (version !== this.#version) return;
      this.#paintState();
      const { path, species, number } = cardQuery(this.#state, this.#species);
      const result = await readJSON(`${API_ORIGIN}${path}`, { signal });
      if (!Array.isArray(result)) throw new Error('Invalid card list');
      const briefs = [...new Map(result.filter(c => validId(c?.id)).map(c => [c.id, c])).values()].slice(0, PAGE_SIZE);
      const cards = [];
      const work = briefs.map((brief, index) => ({ brief, index }));
      let failed = 0;
      const worker = async () => {
        while (work.length) {
          const { brief, index } = work.shift();
          try {
            const cacheKey = `${this.#state.language}:${brief.id}`;
            let card = this.#cache.get(cacheKey);
            if (!card) {
              card = normalCard(await readJSON(`${API_ORIGIN}/v2/${this.#state.language}/cards/${encodeURIComponent(brief.id)}`, { signal }), brief.id);
              if (this.#cache.size >= 180) this.#cache.delete(this.#cache.keys().next().value);
              this.#cache.set(cacheKey, card);
            }
            if ((species || number) && !card.dexIds.includes(species?.id || number)) { failed++; continue; }
            if (this.#state.set && card.set.id !== this.#state.set) { failed++; continue; }
            if (this.#state.rarity && card.rarity !== this.#state.rarity) { failed++; continue; }
            cards[index] = card;
          } catch { if (signal.aborted) return; failed++; }
        }
      };
      await Promise.all([worker(), worker(), worker(), worker()]);
      if (version !== this.#version || signal.aborted) return;
      this.#cards = cards.filter(Boolean);
      const nodes = this.#cards.map(c => this.#cardNode(c, signal));
      this.querySelector('[data-dex-results]').replaceChildren(...nodes);
      if (this.#cards[0]) this.#spotlight(this.#cards.find(c => c.types.length && c.hp) || this.#cards[0]);
      this.querySelector('[data-dex-next]').disabled = briefs.length < PAGE_SIZE;
      this.#status(failed ? 'partial' : this.#cards.length ? 'ready' : 'empty', failed > 0);
      this.querySelector('[data-dex-count]').textContent = `${this.#cards.length} ${this.#message('cards')}`;
    } catch {
      if (version === this.#version && !signal.aborted) { this.#status('unavailable', true); this.querySelector('[data-dex-retry]').hidden = false; }
    } finally {
      if (version === this.#version) this.setAttribute('aria-busy', 'false');
    }
  }

  #image(card, className) {
    if (!card.image) return this.#node('div', `${className} pdx-no-image`, this.#message('no_image'));
    const img = this.#node('img', className); img.src = card.image; img.alt = card.name;
    img.width = 245; img.height = 342; img.loading = 'lazy'; img.decoding = 'async'; img.referrerPolicy = 'no-referrer';
    img.addEventListener('error', () => img.replaceWith(this.#node('div', `${className} pdx-no-image`, this.#message('no_image'))), { once: true });
    return img;
  }

  #spotlight(card, focus = this.querySelector('[data-dex-focus]')) {
    const meta = this.#node('div', 'pdx-focus-meta');
    const classic = focus.hasAttribute('data-dex-focus');
    const requested = this.#species.find(p => normalName(p.name) === normalName(this.#state.q) || String(p.id) === this.#state.q.replace('#', ''));
    const species = classic ? this.#species.find(p => p.id === (requested && card.dexIds.includes(requested.id) ? requested.id : card.dexIds[0])) : null;
    const number = this.querySelector('[data-dex-number]');
    if (classic && number) number.textContent = species ? `#${String(species.id).padStart(4, '0')}` : '—';
    meta.append(this.#node('p', 'pdx-kicker', card.dexIds.map(n => `#${String(n).padStart(4, '0')}`).join(' / ')), this.#node('h2', '', species?.name || card.name));
    const stats = this.#node('dl', 'pdx-stats');
    for (const [label, value] of [[this.#message('type'), card.types.join(' / ')], [this.#message('hp'), card.hp], [this.#message('stage'), card.stage], ...(classic ? [] : [[this.#message('rarity'), card.rarity]])]) {
      if (value === null || value === '') continue;
      const pair = this.#node('div'); pair.append(this.#node('dt', '', label), this.#node('dd', '', value)); stats.append(pair);
    }
    meta.append(stats, this.#node('p', 'pdx-description', card.description || `${card.set.name} · ${card.localId}`));
    const image = this.#image(card, 'pdx-focus-image');
    focus.replaceChildren(image, meta);
    if (classic && species && this.#state.mode === 'classic') this.#sprite(species, focus, image);
  }

  async #sprite(species, host, previous) {
    const group = String(Math.floor((species.id - 1) / 128));
    const source = this.#spriteURLs[group];
    if (!source) return;
    try {
      let data = this.#spriteCache.get(group);
      if (!data) {
        data = await readJSON(source, { external: false, signal: this.#abort.signal, maxBytes: 600000 });
        this.#spriteCache.set(group, data);
      }
      const src = data[String(species.id)];
      if (typeof src !== 'string' || src.length > 44000 || !/^data:image\/png;base64,[A-Za-z0-9+/]+={0,2}$/.test(src) || !host.contains(previous)) return;
      const img = this.#node('img', 'pdx-focus-image pdx-pixel-sprite');
      img.src = src; img.alt = species.name; img.width = 96; img.height = 96; img.decoding = 'async';
      previous.replaceWith(img);
    } catch {}
  }

  #cardNode(card, signal) {
    const article = this.#node('article', 'pdx-card');
    const art = this.#node('button', 'pdx-art'); art.type = 'button'; art.dataset.dexCard = card.id;
    art.setAttribute('aria-label', `${this.#message('details')} ${card.name}`); art.append(this.#image(card, 'pdx-card-image'));
    const body = this.#node('div', 'pdx-card-body');
    body.append(this.#node('p', 'pdx-kicker', `${card.set.name} · ${card.localId}`), this.#node('h3', '', card.name), this.#node('p', 'pdx-rarity', card.rarity || '—'));
    const offer = this.#node('div', 'pdx-offer'); offer.append(this.#node('p', 'pdx-unavailable', this.#message('not_stocked')));
    body.append(offer); article.append(art, body);
    this.#attachOffers(card, offer, signal);
    return article;
  }

  async #attachOffers(card, host, signal) {
    const matched = [];
    for (const template of this.querySelectorAll('template[data-dex-offer]')) {
      const mapping = { setId: template.dataset.set, number: template.dataset.number, cardId: template.dataset.card,
        language: template.dataset.language, verified: template.dataset.verified === 'true', productType: template.dataset.productType };
      if (matchIdentity(card, mapping, this.#state.language)) matched.push(template);
    }
    if (!matched.length) return;
    const entries = [];
    for (const template of matched.slice(0, 5)) {
      try {
        const handle = template.dataset.handle;
        if (!/^[a-z0-9][a-z0-9-]{0,200}$/.test(handle)) continue;
        const url = new URL(`${this.dataset.root}products/${handle}.js`, location.origin);
        if (url.origin !== location.origin) continue;
        const product = await readJSON(url, { external: false, signal, maxBytes: 1000000 });
        if (String(product.id) !== template.dataset.productId || !Array.isArray(product.variants)) continue;
        const fragment = template.content.cloneNode(true);
        const variants = product.variants;
        const variant = variants.length === 1 ? variants[0] : null;
        const price = fragment.querySelector('[data-dex-price]');
        if (variant && Number.isFinite(variant.price)) price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'sv', { style: 'currency', currency: this.dataset.currency }).format(variant.price / 100);
        const stock = fragment.querySelector('[data-dex-stock]');
        stock.textContent = variant ? this.#message(variant.available ? 'in_stock' : 'sold_out') : this.#message('choose_variant');
        stock.dataset.available = String(Boolean(variant?.available));
        const button = fragment.querySelector('button[name="add"]');
        if (button) button.disabled = !variant?.available || String(variant.id) !== template.dataset.variantId;
        entries.push(fragment);
      } catch { if (signal.aborted) return; }
    }
    if (!signal.aborted && host.isConnected && entries.length) host.replaceChildren(...entries);
  }
}

if (!customElements.get('tcg-pokedex')) customElements.define('tcg-pokedex', TcgPokedex);
